import React, { useRef, useEffect, useState, useReducer } from "react";
import * as Styled from "./styled";

import PopupActions from "./reducers/popupActions";
import * as PopupReducer from "./reducers/popupReducer";
import * as MobilePopupReducer from "./reducers/mobilePopupReducer";

import Popup from "../Popup";
import Welcome from "./components/Welcome";
import City from "./components/City";
import Plot from "./components/Plot";
import Wallet from "../Wallet";
import MyLands from "./components/MyLands";
import About from "./components/About";
import MyLandsButton from "../../mobile/components/MyLandsButton";
import ContentBottomSheet from "../../mobile/components/ContentBottomSheet";

import Boroughs from "./helpers/Boroughs";
import Plots from "./helpers/Plots";

import { useDispatch, useSelector } from "react-redux";
import { selectLand, deselectLand } from "../../reducers/hub";
import { fetchOwnedIds, resetOwned, fetchHubDetails } from "../../reducers/hub";
import { isMobile, BrowserView, MobileView } from "react-device-detect";
import { matchPath, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import usePrepareLink from "../../hooks/usePrepareLink";
// MapLibre Webpack production build issue workaround
// https://github.com/maplibre/maplibre-gl-js/issues/1011
// eslint-disable-next-line import/no-webpack-loader-syntax
import maplibregl from "!maplibre-gl";
import maplibreglWorker from "maplibre-gl/dist/maplibre-gl-csp-worker";

import WalletStatus from "../../reducers/wallet/walletStatus";
import { reconnect } from "../../reducers/wallet";
import { resume } from "../../reducers/transactions";
import { MAP_ROUTER_PATH } from "../../const/router";
maplibregl.workerClass = maplibreglWorker;

const newYork = { id: "new-york", lngLat: [-73.997809, 40.719594] };

function Map(props) {
  const dispatch = useDispatch();

  const walletAddress = useSelector((state) => state.wallet.account.address);

  useEffect(() => {
    dispatch(walletAddress ? fetchOwnedIds(walletAddress) : resetOwned());
    dispatch(fetchHubDetails());
  }, [dispatch, walletAddress]);

  // Maplibre

  const mapRef = useRef(null);
  const [map, setMap] = useState(null);

  const [plotsHelper, setPlotsHelper] = useState(null);
  const [, setBoroughsHelper] = useState(null);
  const [showSheet, setShowSheet] = useState(false);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_MAPTILER_API_KEY;
    const mapId = "9c471648-fb21-4417-ad35-28e4739b09bf";

    const map = new maplibregl.Map({
      container: mapRef.current,
      style: `https://api.maptiler.com/maps/${mapId}/style.json?key=${apiKey}`,
      center: newYork.lngLat,
      zoom: 12,
    });

    map.on("load", () => {
      setBoroughsHelper(new Boroughs(map, apiKey));
      setPlotsHelper(new Plots(map, apiKey, handlePlotSelect));
    });

    setMap(map);

    return () => {
      map.remove();
    };
  }, []);

  // Popups

  const [popupState, popupDispatch] = useReducer(
    isMobile ? MobilePopupReducer.reducer : PopupReducer.reducer,
    isMobile ? MobilePopupReducer.initialState : PopupReducer.initialState
  );

  const cityPath = usePrepareLink(MAP_ROUTER_PATH.city).pathname;
  const plotPath = usePrepareLink(MAP_ROUTER_PATH.plot).pathname;
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    let isHomePath = matchPath(MAP_ROUTER_PATH.home, location.pathname);
    if (isHomePath != null) {
      popupDispatch({ type: PopupActions.welcomeOpen });
    }
  }, [location.pathname]);

  // Router navigation
  useEffect(() => {
    const landId =
      popupState.selectedPlot?.properties?.id ?? matchPath(MAP_ROUTER_PATH.plot, location.pathname)?.params?.id ?? null;

    if (landId) {
      if (isMobile) setShowSheet(true);
      dispatch(selectLand(landId));
    } else {
      if (popupState.displayPopupPlot && isMobile) {
        setShowSheet(false);
      }
      dispatch(deselectLand());
    }

    if (popupState.displayPopupCity) {
      navigate(cityPath.replace(MAP_ROUTER_PATH.city, `/${newYork.id}`), { replace: true });
      plotsHelper.selectZone(null);
    } else if (isMobile) {
      switch (true) {
        case popupState.displayPopupWallet: {
          navigate(MAP_ROUTER_PATH.wallet);
          break;
        }
        case popupState.displayPopupAbout: {
          navigate(MAP_ROUTER_PATH.about);
          break;
        }
        case popupState.displayPopupMyLands: {
          navigate(MAP_ROUTER_PATH.myLands);
          break;
        }
        default:
          console.log("Unhandled popupState: " + popupState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, popupState]);

  const popupWelcome = (
    <Popup modal={isMobile ? "modal" : null} onClose={() => handlePopupWelcomeClose()}>
      <Welcome onExplore={() => handlePopupWelcomeClose()} />
    </Popup>
  );

  const handlePopupWelcomeClose = () => {
    popupDispatch({ type: PopupActions.welcomeClose });
    const target = { center: newYork.lngLat, zoom: 13 };
    map.flyTo(target);
  };

  // City popup

  const popupCity = (
    <Popup modal>
      <City onSelectZone={(id) => plotsHelper.selectZone(id)} onOpenAbout={() => handlePopupAboutOpen()} />
    </Popup>
  );

  // Plot popup

  const popupPlot = (
    <Popup onClose={() => handlePlotClose()}>
      <Plot onOpenConnectWallet={(isVisible) => props.onDisplayPopupWallet(isVisible)} />
    </Popup>
  );

  const handlePlotClose = () => {
    popupDispatch({ type: PopupActions.plotClose });
    plotsHelper.removeMarker();
  };

  const handlePlotSelect = (feature) => {
    if (feature) {
      popupDispatch({ type: PopupActions.plotSelect, payload: feature });
    } else {
      popupDispatch({ type: PopupActions.plotClose });
    }
  };

  // About popup
  const [displayPopupAbout, setDisplayPopupAbout] = useState(false);

  const popupAbout = <About onClose={() => handlePopupAboutClose()} />;

  const handlePopupAboutOpen = () => {
    setDisplayPopupAbout(true);
    if (isMobile) popupDispatch({ type: PopupActions.aboutOpen });
  };

  const handlePopupAboutClose = () => {
    setDisplayPopupAbout(false);
    if (isMobile) popupDispatch({ type: PopupActions.cityOpen });
  };

  // Wallet popup
  const walletStatus = useSelector((state) => state.wallet.status);

  useEffect(() => {
    if (props.displayPopupWallet && isMobile) {
      popupDispatch({ type: PopupActions.walletOpen });
      setShowSheet(true);
    }

    switch (walletStatus) {
      case WalletStatus.idle:
        dispatch(reconnect());
        break;

      case WalletStatus.connected:
        if (props.displayPopupWallet && isMobile) {
          popupDispatch({ type: PopupActions.cityOpen });
        }
        props.onDisplayPopupWallet(false);
        dispatch(resume());
        break;

      default:
        break;
    }
  }, [dispatch, walletStatus, props]);

  const popupWallet = (
    <Wallet
      onClose={() => {
        props.onDisplayPopupWallet(false);
        if (isMobile) {
          popupDispatch({ type: PopupActions.cityOpen });
          setShowSheet(true);
        }
      }}
    />
  );

  useEffect(
    () => {
      const feature = popupState.selectedPlot;
      if (feature) {
        const marker = plotsHelper.getMarker();
        const center = marker.center ?? feature.geometry.coordinates[0][0];
        const target = {
          center: center,
          duration: 600,
          zoom: 14,
          essential: true,
        };
        map.flyTo(target);
        navigate(
          plotPath
            .replace(MAP_ROUTER_PATH.city, `/${newYork.id}`)
            .replace(":id", popupState.selectedPlot.properties.id),
          {
            replace: true,
          }
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, plotsHelper, popupState.selectedPlot]
  );

  // My Lands

  const ownedLandsIds = useSelector((state) => state.hub.owned?.ids);
  const handleLandDetails = (landId) => {
    plotsHelper.selectPlot(landId);
  };

  return (
    <>
      <BrowserView>
        <Styled.Container>
          <Styled.Map ref={mapRef} />
          {ownedLandsIds?.length > 0 && (
            <MyLands landsIds={ownedLandsIds} onClose={() => {}} onLandDetails={handleLandDetails} />
          )}
          {props.displayPopupWallet && popupWallet}
          {popupState.displayPopupWelcome && popupWelcome}
          {displayPopupAbout && popupAbout}

          <Routes>
            <Route path={cityPath} element={popupCity} />
            <Route path={plotPath} element={popupPlot} />
          </Routes>
        </Styled.Container>
      </BrowserView>

      <MobileView>
        <Styled.Container>
          <Styled.Map ref={mapRef} />
          {ownedLandsIds?.length > 0 && (
            <MyLandsButton
              onExpand={() => {
                popupDispatch({ type: PopupActions.myLandsOpen });
                setShowSheet(true);
              }}
            />
          )}

          <ContentBottomSheet
            showSheet={showSheet}
            onShowSheet={(isVisible) => {
              setShowSheet(isVisible);
            }}
            displayPopupCity={popupState.displayPopupCity}
          >
            {popupState.displayPopupWelcome && popupWelcome}
            <Routes>
              <Route
                path={MAP_ROUTER_PATH.myLands}
                element={
                  ownedLandsIds?.length > 0 && (
                    <MyLands
                      landsIds={ownedLandsIds}
                      onLandDetails={handleLandDetails}
                      onClose={() => {
                        popupDispatch({ type: PopupActions.cityOpen });
                      }}
                    />
                  )
                }
              />
              <Route path={cityPath} element={popupCity} />
              <Route path={plotPath} element={popupPlot} />
              <Route path={MAP_ROUTER_PATH.wallet} element={popupWallet} />
              <Route path={MAP_ROUTER_PATH.about} element={popupAbout} />
            </Routes>
          </ContentBottomSheet>
        </Styled.Container>
      </MobileView>
    </>
  );
}

export default Map;
